import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import {
  AffectedTrainsResponse,
  AffectedTrainsResponseSchema,
} from "features/CenterContent/RoleContent/Vaktleder/types";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { useRef } from "react";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

export const useAffectedTrains = () => {
  const countryCode = useCountryCodeForDropsRole();

  const affectedTrainsResult = useQuery({
    queryKey: ["affectedTrainsList", countryCode],
    queryFn: ({ signal }) =>
      axiosClient
        .get<AffectedTrainsResponse>(
          `${getBackendUrl()}/affected-trains/${countryCode}`,
          {
            signal,
          },
        )
        .then((res) => {
          const { error } = AffectedTrainsResponseSchema.safeParse(res.data);
          if (error) {
            log(
              LogLevel.error,
              "AffectedTrains response schema error",
              error.message,
            );
          }
          return AffectedTrainsResponseSchema.parse(res.data);
        }),
    refetchInterval: 5 * 60 * 1000, // Every 5 minutes
  });

  const handleInfraWebsocketMessage = useRef(() => {
    // It takes a while to calculate affected trains, so we delay the refetch a bit.
    // This is a best-effort and there is no guarantee that affected trains is ready by the time
    // we refetch.
    window.setTimeout(() => {
      void affectedTrainsResult.refetch();
    }, 7_000);
  });

  const { connected } = useWebsocket(BackendIdentifier.TRAINMAP_BACKEND, {
    [WebsocketTopic.INFRASTRUCTURE_EVENT]: handleInfraWebsocketMessage.current,
  });

  return {
    ...affectedTrainsResult,
    refetch: affectedTrainsResult.refetch,
    websocketConnected: connected,
  };
};
